import * as Sentry from '@sentry/nextjs';

import Chart, { ChartEvent, LegendElement, LegendItem } from 'chart.js/auto';
import { format, lastDayOfMonth, parse, subMonths, isSameDay } from 'date-fns';

import {
    IGetCommonStatData,
    IGetCommonStatDataAttributes,
    IGetCommonStatDataFormatted,
    IGetCommonStatSummaryDataAttributes,
    IGetPlacementsDataFormatted,
    IGetSubsDataFormatted,
} from '@api/types';
import {
    ICommonFilterValues,
    PrepareSubsSelectOptionsType,
    PrepareSubsSelectOptionType,
} from '@pages/statistics/common/interfaces';
import { getCurrencySign } from '@utils/currency';
import { chartLineOptions, columnByType, datePeriods } from './config';

const defaultLegendClickHandler: (
    this: LegendElement,
    e: ChartEvent,
    legendItem: LegendItem,
    legend: LegendElement,
) => void = Chart.defaults.plugins.legend.onClick;

// возьмем ключи chartLineOptions как id для легенд графика
const chartLegendsIds: string[] = Object.keys(chartLineOptions);

/**
 * Сохраняем задизеибленные значения графика в localStorage
 */
export function toggleLegendIndexInLocalStorage(
    e: ChartEvent,
    legendItem: LegendItem,
    legend: LegendElement,
): void {
    const { hidden, datasetIndex: index } = legendItem;
    // у legendItem есть только index, сопоставим его с ключом из chartLegendsIds
    const legendId: string = chartLegendsIds[index];

    if (typeof window !== 'undefined') {
        let disabledChartLegends = JSON.parse(localStorage.getItem('disabledChartLegends') || '[]');

        if (hidden) {
            disabledChartLegends = disabledChartLegends.filter(id => id !== legendId);
        } else {
            disabledChartLegends.push(legendId);
        }

        localStorage.setItem('disabledChartLegends', JSON.stringify(disabledChartLegends));
        // defaultLegendClickHandler требует передачу this при типизации
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        defaultLegendClickHandler(e, legendItem, legend);
    }
}

export const prepareSubsSelectOptions = (
    subs: IGetSubsDataFormatted[],
): PrepareSubsSelectOptionsType => {
    const preparedSubsObj = subs.reduce(
        (subsValues: PrepareSubsSelectOptionsType, { subs: subsObj }: IGetSubsDataFormatted) => {
            // eslint-disable-next-line consistent-return
            Object.keys(subsObj).forEach((subName: string): void => {
                const prevValue: PrepareSubsSelectOptionType[] = subsValues[subName];
                const newValue: string = subsObj[subName];

                if (newValue) {
                    const newValueAsSelectOption: PrepareSubsSelectOptionType = {
                        label: newValue,
                        value: newValue,
                    };

                    // eslint-disable-next-line no-param-reassign
                    subsValues[subName] = prevValue
                        ? [...prevValue, newValueAsSelectOption]
                        : [newValueAsSelectOption];
                }
            });

            return subsValues;
        },
        {},
    );

    // Генерим первые значения сабов
    const firstValuesObj = Object.keys(preparedSubsObj).reduce(
        (acc: PrepareSubsSelectOptionsType, key: string): PrepareSubsSelectOptionsType => ({
            ...acc,
            [key]: [{ label: key.toUpperCase(), value: null }],
        }),
        {},
    );
    // Добавляем новые значения
    Object.keys(preparedSubsObj).forEach((key: string): void => {
        // Фильтр только уникальных сабов
        const subsArray: Map<string, PrepareSubsSelectOptionType> = new Map();
        if (Array.isArray(preparedSubsObj[key])) {
            preparedSubsObj[key].forEach(sub => subsArray.set(sub.label, sub));
        }
        preparedSubsObj[key] = [...firstValuesObj[key], ...Array.from(subsArray.values())];
    });

    return preparedSubsObj;
};

export type FiltersConvertedToParams = Omit<Partial<ICommonFilterValues>, 'dateFrom' | 'dateTo'> & {
    dateFrom: string;
    dateTo: string;
};

export const convertFilterIntoParams = (
    curFilters: ICommonFilterValues,
): FiltersConvertedToParams => {
    const { dateFrom, dateTo } = curFilters;
    const dateFromConverted: string = format(dateFrom, 'yyyy-MM-dd');
    const dateToConverted: string = format(dateTo, 'yyyy-MM-dd');

    const onlyValidFilters: Partial<ICommonFilterValues> = Object.keys(curFilters).reduce(
        (acc, current) => ({
            ...acc,
            ...(curFilters[current] && { [current]: curFilters[current] }),
        }),
        {},
    );
    return {
        ...onlyValidFilters,
        dateFrom: dateFromConverted,
        dateTo: dateToConverted,
    };
};

export const addCurrencySign = (
    value: string | number,
    currency: 'USD' | 'RUB' | 'EUR' | 'GBP' | 'TON',
): string => `${value} ${getCurrencySign(currency, 'sign')}`;

export const addSignsTotal = (
    total: IGetCommonStatSummaryDataAttributes,
    currency: 'USD' | 'RUB' | 'EUR' | 'GBP' | 'TON',
): IGetCommonStatDataAttributes => {
    const {
        clicks,
        hosts,
        leadsHold,
        leads,
        signups,
        epc = 0,
        revenue = 0,
        revenueHold = 0,
        turnover = 0,
    } = total;
    let { cr = 0 } = total;

    const epcFormatted = addCurrencySign(epc, currency);
    const revenueFormatted = addCurrencySign(revenue, currency);
    const revenueHoldFormatted = addCurrencySign(revenueHold, currency);
    const turnoverFormatted = addCurrencySign(turnover, currency);
    cr = `${cr}%`;

    const newTotal: IGetCommonStatDataAttributes = {
        clicks,
        hosts,
        ...(signups !== undefined && { signups }),
        cr,
        epc: epcFormatted,
        turnover: turnoverFormatted,
        leadsHold,
        leads,
        revenueHold: revenueHoldFormatted,
        revenue: revenueFormatted,
    };

    return newTotal;
};

export const convertObjValuesToDate = (dates: {
    dateFrom: Date;
    dateTo: Date;
}): { [key: string]: Date } | null => {
    if (!dates) return null;

    return Object.keys(dates).reduce(
        (
            acc: { [key: string]: Date },
            date: string,
        ): {
            [key: string]: Date;
        } => ({
            ...acc,
            [date]: new Date(dates[date]),
        }),
        {},
    );
};

export const formatCommonStat = (data: IGetCommonStatData[]): IGetCommonStatDataFormatted[] => {
    return (
        data?.map(
            ({
                id,
                attributes: {
                    cr,
                    epc,
                    impressions,
                    clicks,
                    hosts,
                    leads,
                    turnover,
                    leadsHold,
                    revenueHold,
                    revenue,
                    date,
                    offerId,
                    creativeId,
                    creative_name: creativeName,
                    countryCode,
                    os,
                    material,
                    placement_id: placementId,
                    sub,
                    sub2,
                    sub3,
                    sub4,
                    sub5,
                    signups,
                },
            }): IGetCommonStatDataFormatted => ({
                id,
                cr: Number(cr),
                epc: Number(epc),
                impressions,
                clicks,
                hosts,
                signups,
                leads,
                turnover,
                leadsHold,
                revenueHold,
                revenue,
                ...(sub && { sub }),
                ...(sub2 && { sub2 }),
                ...(sub3 && { sub3 }),
                ...(sub4 && { sub4 }),
                ...(sub5 && { sub5 }),
                ...(date && { date }),
                ...(offerId && { offerId }),
                ...(creativeId !== undefined && { creativeId }),
                ...(creativeName && { creativeName }),
                // Надо детектить неопределенные значения (приходят пустой строкой)
                ...((countryCode && { countryCode }) || (countryCode === '' && { countryCode })),
                ...((os && { os }) || (os === '' && { os })),
                ...((material && { material }) || (material === '' && { material })),
                ...(placementId === '0' ? { placementId: '' } : { placementId }),
            }),
        ) || []
    );
};

export const reducePlacementStat = (
    data: IGetCommonStatDataFormatted[],
    filters: ICommonFilterValues,
): IGetCommonStatDataFormatted[] => {
    const currentTab = columnByType[filters.type];

    return (
        data?.reduce((acc, item) => {
            const sameRows = acc.filter(row => row[currentTab] === item[currentTab]);

            if (sameRows.length) {
                const reducedSameRows = sameRows.reduce((row, current) => {
                    const { [currentTab]: type, ...other } = current;

                    // складываем значения в одинаковых строках
                    return Object.keys(other).reduce(
                        (newRow, key) => ({
                            ...newRow,
                            [type]: current[type],
                            [key]: row[key]
                                ? Number(current[key]) + Number(row[key])
                                : Number(current[key]),
                        }),
                        {},
                    );
                }, {});

                acc.push(reducedSameRows);
            } else {
                acc.push(item);
            }
            return acc;
        }, []) || []
    );
};

// удаляем слэш чтобы например https://henderson.ru/ и https://henderson.ru считались одинаковыми
const clearSlash = (url: string): string => {
    if (url?.[url.length - 1] === '/') {
        return url.slice(0, -1);
    }
    return url;
};

export const getUniqPlacementsOptions = (
    placements: IGetPlacementsDataFormatted[],
): {
    label: string;
    value: string;
    ids: string;
}[] => {
    const uniqPlacements = placements.reduce(
        (
            acc: {
                [key: string]: string;
            },
            { id, url }: IGetPlacementsDataFormatted,
        ) => {
            const clearUrl: string = clearSlash(url);
            const newAcc: { [key: string]: string } = {
                ...acc,
                [clearUrl]: acc[clearUrl] ? `${acc[clearUrl]},${id}` : id,
            };
            return newAcc;
        },
        {},
    );

    const options = Object.keys(uniqPlacements).map((key: string): {
        label: string;
        value: string;
        ids: string;
    } => ({
        label: key,
        value: key,
        ids: uniqPlacements[key],
    }));

    return options;
};

/**
 * @param {string} date
 * @param {string} key
 * @returns { dateFrom: Date; dateTo: Date; }
 */
export const getDateRange = (
    date: string,
    key: string,
): {
    dateFrom: Date;
    dateTo: Date;
} => {
    const MONTH_RANGE = 1;

    try {
        switch (key) {
            case 'month': {
                const currentDate: Date = new Date();
                const lastDayOfCurrentMonth: Date = lastDayOfMonth(new Date(date));
                const isLastDateOfCurrentMonth: boolean = lastDayOfCurrentMonth > currentDate;
                const correctDate: Date = isLastDateOfCurrentMonth
                    ? currentDate
                    : lastDayOfCurrentMonth;

                const dateFrom: Date = subMonths(correctDate, MONTH_RANGE);
                const dateTo: Date = correctDate;

                return { dateFrom, dateTo };
            }
            default: {
                const parsedDate: Date = parse(date, 'dd MMM yyyy', new Date());
                return { dateFrom: parsedDate, dateTo: parsedDate };
            }
        }
    } catch (err) {
        Sentry.captureException(err);

        return {
            dateFrom: new Date(),
            dateTo: new Date(),
        };
    }
};

type DatePeriodKey = keyof typeof datePeriods;

export const getPeriodByDates = (dateFrom: string, dateTo: string): DatePeriodKey | null => {
    const from = parse(dateFrom, 'MM-dd-yyyy', new Date());
    const to = parse(dateTo, 'MM-dd-yyyy', new Date());

    return (
        (Object.keys(datePeriods) as DatePeriodKey[]).find(
            key =>
                isSameDay(datePeriods[key].dateFrom, from) &&
                isSameDay(datePeriods[key].dateTo, to),
        ) ?? null
    );
};
